import React, { useMemo, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";
import { DragProvider } from "@activecollab/yank-and-drop";
import PropTypes from "prop-types";
import { AppRealtimeProvider } from "~/components/AppRealtimeProvider";
import { BrowserTabProvider } from "~/components/BrowserTab/BrowserTabProvider";
import { ChatControlsProvider } from "~/components/Chat/context/ChatControlsProvider";
import { FileTokenProvider } from "~/components/FilePreview";
import { FilePreviewProvider } from "~/components/FilePreview/FilePreviewProvider";
import { LinkProvider } from "~/components/LinkProvider";
import { themes } from "~/components/MyPreferences/themes";
import CreateTaskProvider from "~/components/Providers/CreateTask";
import { HotJarProvider } from "~/components/Providers/HotJar/HotJarProvider";
import { IntercomProvider } from "~/components/Providers/Intercom/IntercomProvider";
import RecurringTaskProvider from "~/components/Providers/RecurringTask";
import { ObjectSheet } from "~/components/Sheet/ObjectSheet";
import { SoundProvider } from "~/components/Sounds/SoundProvider";
import { TickerProvider } from "~/components/Ticker/TickerProvider";
import { StopwatchProvider } from "~/components/Timer/StopwatchProvider";
import { UploadProvider } from "~/components/UploadProvider/UploadProvider";
import { UserCardProvider } from "~/components/UserCard/UserCardProvider";
import { UserRealTimeChannel } from "~/components/UserRealTime/UserRealTimeChannel";
import { UserRealTimeListener } from "~/components/UserRealTime/UserRealTimeListener";
import { VisualEditorProvider } from "~/components/VisualEditor/VisualEditorProvider";
import WhoIsOnlineProvider from "~/components/WhoIsOnline/index.jsx";
import SubscriptionPage from "~/pages/Subscription/SubscriptionPage";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { makeGetAccountStatus } from "~/store/selectors/selector.configOptions.jsx";
import { HistoryProvider } from "./HistoryProvider";

export const Providers = ({ children }) => {
  const getAccountStatus = useMemo(makeGetAccountStatus, []);
  const accountStatus = useSelector(getAccountStatus);
  const currentTheme = useSelector((state) => getRawPersonalizedConfigOption(state, "theme"));

  useEffect(() => {
    const html = document.querySelector("html").classList;
    if (currentTheme) {
      themes.forEach((theme) => {
        if (html.contains(theme.name) && theme.name !== currentTheme) {
          html.remove(theme.name);
        }
      });
      html.add(currentTheme);
    }
    return () => {
      currentTheme && html.remove(currentTheme);
    };
  }, [currentTheme]);

  const isAccountActive = accountStatus !== "retired";

  return (
    <HistoryProvider>
      <AppRealtimeProvider>
        <BrowserTabProvider>
          <UserRealTimeChannel>
            <UserRealTimeListener />
            <WhoIsOnlineProvider>
              <SoundProvider>
                <ChatControlsProvider>
                  <DndProvider backend={HTML5Backend}>
                    <DragProvider>
                      <UploadProvider>
                        <FileTokenProvider>
                          <VisualEditorProvider>
                            <TickerProvider>
                              <StopwatchProvider>
                                <HotJarProvider>
                                  <IntercomProvider>
                                    <UserCardProvider>
                                      <FilePreviewProvider>
                                        <LinkProvider>
                                          {isAccountActive ? (
                                            <RecurringTaskProvider>
                                              <CreateTaskProvider>
                                                {children}
                                                <ObjectSheet />
                                              </CreateTaskProvider>
                                            </RecurringTaskProvider>
                                          ) : (
                                            <SubscriptionPage />
                                          )}
                                        </LinkProvider>
                                      </FilePreviewProvider>
                                    </UserCardProvider>
                                  </IntercomProvider>
                                </HotJarProvider>
                              </StopwatchProvider>
                            </TickerProvider>
                          </VisualEditorProvider>
                        </FileTokenProvider>
                      </UploadProvider>
                    </DragProvider>
                  </DndProvider>
                </ChatControlsProvider>
              </SoundProvider>
            </WhoIsOnlineProvider>
          </UserRealTimeChannel>
        </BrowserTabProvider>
      </AppRealtimeProvider>
    </HistoryProvider>
  );
};

Providers.propTypes = {
  children: PropTypes.any,
};
